import React, { useState, useRef, useEffect } from "react";
import {
  PlayFill,
  PauseFill,
  Ellipsis,
  ArrowShapeDownToLine,
  ArrowUturnCwRight,
  ChevronDown,
  ArrowsRotateRight,
  PencilToLine,
} from "@gravity-ui/icons";
import { useAudioPlayer } from "../../contexts/AudioPlayerContext";
import { useLocalState } from "../../contexts/LocalStateProvider"; // Import the local state hook
import apiService from "../../api/apiService";
import Toast from "../generic_components/Toast"; // Import the Toast component
import ProgressInfoToast from "../custom_components/ProgressInfoToast"; // Add the ProgressInfoToast import
import AudioProcessor from "../custom_components/AudioProcessor";
import { usePostHog } from "posthog-js/react";
import DownloadModal from "./DownloadModal"; // Import DownloadModal
import { getCookie } from "../../utils/cookieUtils"; // Import getCookie
import * as Sentry from "@sentry/react"
import { getPartitionedLocalStorage } from "../../utils/tokenExtraction";

const Version = ({ key, version, index, versionCount, trackIndexToShow }) => {
  const posthog = usePostHog();
  const [isExpanded, setIsExpanded] = useState(index === 0);
  const [showDropdown, setShowDropdown] = useState(Array(version?.versions?.length).fill(false));

  useEffect(() => {
    if (
      version?.hasVariations &&
      version?.versions?.length > 1 &&
      index === 0
    ) {
      setIsExpanded(true);
    }
  }, [version?.versions?.length]);

  // console.log("key:", key, "Version :", version, "\nindex:", index, "\nversionCount:", versionCount);;

  // For single versions without variations
  if (!version.hasVariations) {
    return (
      <SingleVersion
        version={version.latest}
        showVersionIcon={false}
        isLastItem={index === versionCount}
        isSecondLastItem={index === versionCount - 1}
        trackIndexToShow={trackIndexToShow}
      />
    );
  }

  // For groups with variations, find the selected version for the header
  let selectedHeaderVersion = version.latest;
  if (
    version.hasVariations &&
    typeof version.latest?.selected_track_id !== "undefined" &&
    Array.isArray(version.versions)
  ) {
    const found = version.versions.find(
      (v) => String(v.ai_response_id) === String(version.latest.selected_track_id)
    );
    if (found) selectedHeaderVersion = found;
  }

  // console.log("Selected Header Version:", selectedHeaderVersion);

  // For groups with variations, show latest version in header
  return (
    <div>
      <div
        onClick={() => {
          // isExpanded && posthog.capture("Expanded version history dropdown");
          setIsExpanded(!isExpanded);
        }}
        className="flex items-center justify-between w-full"
      >
        {/* Header shows only latest version's details */}
        <SingleVersion
          version={selectedHeaderVersion}
          showVersionIcon={false}
          isHeader={true}
          versionCountText={version.versions.length > 1 ? `${version.versions.length} versions` : null}
          isExpanded={isExpanded}
          trackIndexToShow={trackIndexToShow}
        />
      </div>

      {/* When expanded, show all versions in descending order */}
      {isExpanded && (
        <div className="ml-2">
          {version.versions.map((v, idx) => (
            <SingleVersion
              key={v.ai_response_id || idx}
              version={v}
              parentGroupId={version.latest.ai_response_id}
              versions={version.versions} // Pass all variations
              showVersionIcon={true}
              isLastItem={idx === version.versions.length - 1}
              isSecondLastItem={idx === version.versions.length - 2}
              trackIndexToShow={trackIndexToShow}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SingleVersion = ({ version, showVersionIcon, isLastItem, isSecondLastItem = false, trackIndexToShow, isHeader = false, versionCountText, isExpanded }) => {
  const {
    isPlaying: isContextPlaying,
    playAudio,
    pauseAudio,
    audioSrc: currentAudioSrcFromContext,
    aiResponseId: contextAiResponseId,
    setDiscoverId,
    setAiResponseId,
    isGlobalBuffering: isGlobalBuffering,

  } = useAudioPlayer();
  const posthog = usePostHog();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const { updateLocalState, localState } = useLocalState();
  const [toastMessage, setToastMessage] = useState(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isGeneratingStems, setIsGeneratingStems] = useState(false);
  const [stemProgress, setStemProgress] = useState(0);
  const [stemTrackName, setStemTrackName] = useState('');
  const [toastType, setToastType] = useState("error");
  const [isUpdatingSelection, setIsUpdatingSelection] = useState(false);

  // Ensure version is defined
  if (!version) {
    return null;
  }

  const {
    audio: { name, duration, cover } = {},
    musicDescription,
    avatar,
    ai_response_id,
    responseType,
    regenerated,
    parent_response_id,
    created_at,
    requestId, // Expect requestId from version object
    request_id, // Fallback for request_id
  } = version;

  const currentTrackIndex = (typeof trackIndexToShow === 'number' && trackIndexToShow >= 0 && trackIndexToShow <= 2) ? trackIndexToShow : 0;
  // console.log("Current Track Index:", currentTrackIndex); // Original log commented out
  // Determine the effective request ID for constructing the stream URL, similar to AIChatBox
  const effectiveStreamSegmentId = requestId || request_id || ai_response_id;

  // GCS (completed track) URL format
  const gcsUrlForThisVersionTrack = ai_response_id
    ? `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-audio/${ai_response_id}/${currentTrackIndex}`
    : null;

  // Chunked (streaming/loading track) URL format
  const chunkStreamUrlForThisVersionTrack = ai_response_id && effectiveStreamSegmentId
    ? `${process.env.REACT_APP_SERVER_ENDPOINT}/api/audio-stream/${effectiveStreamSegmentId}/${currentTrackIndex}`
    : null;

  const isThisVersionPlaying = 
    isContextPlaying && 
    contextAiResponseId === ai_response_id &&
    (currentAudioSrcFromContext === gcsUrlForThisVersionTrack || currentAudioSrcFromContext === chunkStreamUrlForThisVersionTrack);


  // console.log(`[SingleVersion ${ai_response_id}/${currentTrackIndex}] isThisVersionPlaying: ${isThisVersionPlaying}. Context: isPlaying=${isContextPlaying}, src=${currentAudioSrcFromContext}, id=${contextAiResponseId}. URLs: gcs=${gcsUrlForThisVersionTrack}, chunk=${chunkStreamUrlForThisVersionTrack}`);

  // Determine if actions should be disabled
  const selectedTrackDetails = localState.selectedTrackDetails;
  const isThisVersionSelectedInChat =
    selectedTrackDetails &&
    selectedTrackDetails.responseId === ai_response_id &&
    selectedTrackDetails.trackIndex === currentTrackIndex;

  const isActionDisabled = isGlobalBuffering
  //  ||  !isThisVersionSelectedInChat;
  const versionHistory = localState?.versionHistory?.[0];
  const isCurrentTrack = localState.selectedTrackDetails?.responseId === localState.ai_response_id;

  const isOldVersion =
    versionHistory?.versions?.length > 0;

  const isActionEnabled =
    ((isCurrentTrack && localState?.selectedTrackDetails !==null) || (isOldVersion && localState.selectedTrackDetails === null && !isCurrentTrack)) &&
    !isGlobalBuffering;

  console.log("isActionEnabled from version:", isActionEnabled);

  useEffect(() => {
    if (!isHeader) {
      console.log(
        `[SingleVersion ${ai_response_id}] Props received - trackIndexToShow: ${trackIndexToShow}. Derived currentTrackIndex: ${currentTrackIndex}, streamUrlForThisVersionTrack: ${chunkStreamUrlForThisVersionTrack}`
      );
    }
  }, [version, trackIndexToShow, currentTrackIndex, chunkStreamUrlForThisVersionTrack, isHeader, ai_response_id]);

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;

    const diffInMinutes = Math.floor(diffMs / (1000 * 60));
    const diffInHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes === 1) return "1 minute ago";
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInHours === 1) return "1 hour ago";
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInDays === 1) return "1 day ago";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInWeeks === 1) return "1 week ago";
    if (diffInWeeks < 4) return `${diffInWeeks} weeks ago`;
    if (diffInMonths === 1) return "1 month ago";
    return `${diffInMonths} months ago`;
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

function findSelectedTrackIdByAiResponseId(obj, targetId) {
  if (typeof obj !== 'object' || obj === null) return;

  if (obj.ai_response_id === targetId && obj.selected_track_id !== undefined) {
    return obj.selected_track_id;
  }

  if (Array.isArray(obj)) {
    for (const item of obj) {
      const result = findSelectedTrackIdByAiResponseId(item, targetId);
      if (result !== undefined) return result;
    }
  } else {
    for (const key in obj) {
      const result = findSelectedTrackIdByAiResponseId(obj[key], targetId);
      if (result !== undefined) return result;
    }
  }
}

  const handlePlayPauseClick = async (e) => {
    e.stopPropagation();
    if (!version || (!gcsUrlForThisVersionTrack && !chunkStreamUrlForThisVersionTrack) || isUpdatingSelection) {
      console.log(`[SingleVersion PlayPause ${ai_response_id}/${currentTrackIndex}] SKIPPED. Version: ${!!version}, GCS_URL: ${!!gcsUrlForThisVersionTrack}, Chunk_URL: ${!!chunkStreamUrlForThisVersionTrack}, UpdatingSelection: ${isUpdatingSelection}`);
      return;
    }
    console.log(`[SingleVersion PlayPause ${ai_response_id}/${currentTrackIndex}] CALLED. isThisVersionPlaying (derived): ${isThisVersionPlaying}. Context isPlaying: ${isContextPlaying}, Context currentAudioSrc: ${currentAudioSrcFromContext}`);
    const obj = localState?.versionHistory;
    const trackId = findSelectedTrackIdByAiResponseId(obj, ai_response_id);
    console.log(`[SingleVersion PlayPause ${ai_response_id}/${currentTrackIndex}] Resolved trackId from versionHistory for play: ${trackId}. (Using currentTrackIndex ${currentTrackIndex} as fallback if trackId is null/undefined for API call)`);

    if (isThisVersionPlaying) {
      console.log(`[SingleVersion PlayPause ${ai_response_id}/${currentTrackIndex}] Action: PAUSE. Calling pauseAudio().`);
      pauseAudio();
    } else {
      console.log(`[SingleVersion PlayPause ${ai_response_id}/${currentTrackIndex}] Action: PLAY. isUpdatingSelection will be set to true.`);
      // Logic to play: update DB then set localState request
      setIsUpdatingSelection(true);
      setToastMessage(null);
      try {
        const trackIdToUpdateInDB = trackId !== null && trackId !== undefined ? trackId : currentTrackIndex;
        console.log(`[SingleVersion PlayPause ${ai_response_id}/${currentTrackIndex}] Updating DB: response ${ai_response_id} to selected_track_id ${trackIdToUpdateInDB}.`);
        await apiService.updateSelectedTrack({
          responseId: ai_response_id,
          trackId: trackIdToUpdateInDB, // Use the resolved or fallback trackId
        });
        console.log(`[SingleVersion PlayPause ${ai_response_id}/${currentTrackIndex}] DB updated. Requesting play via localState for trackIndex ${trackIdToUpdateInDB}.`);

        // Update localState to request AIChatBox to play this track
        updateLocalState("playRequestFromHistory", {
          responseId: ai_response_id,
          trackIndex: trackIdToUpdateInDB, // Use the resolved or fallback trackId
          timestamp: Date.now()
        });

        // Update currentSongDetails in localState as well for the audio player UI
        const detailsData = {
          name,
          description: musicDescription,
          coverImage: cover,
          duration,
          audioSrc: chunkStreamUrlForThisVersionTrack || gcsUrlForThisVersionTrack, // Use the stream URL
          ai_response_id,
          projectId: version.projectId,
          responseType,
          regenerated,
          created_at,
          selectedTrackIndexForPreview: currentTrackIndex
        };
        updateLocalState("currentSongDetails", detailsData);
        updateLocalState("audioImgSrc", cover);
        console.log(`[SingleVersion PlayPause ${ai_response_id}/${currentTrackIndex}] Set context discoverId to null, aiResponseId to ${ai_response_id}.`);
        setDiscoverId(null); // Ensure discoverId is cleared
        setAiResponseId(ai_response_id); // Set aiResponseId for the player context

      } catch (error) {
        console.error(`[SingleVersion PlayPause ${ai_response_id}/${currentTrackIndex}] Error updating DB or setting local state:`, error);
        Sentry.captureException(error);
        setToastMessage("Error preparing track for playback. Please try again.");
        setToastType("error");
      } finally {
        console.log(`[SingleVersion PlayPause ${ai_response_id}/${currentTrackIndex}] Setting isUpdatingSelection to false.`);
        setIsUpdatingSelection(false);
      }
    }
  };

  const handleVersionItemClick = async (e) => {
    if (isHeader || !version || (!gcsUrlForThisVersionTrack && !chunkStreamUrlForThisVersionTrack) || isUpdatingSelection) {
      console.log(`[SingleVersion ItemClick ${ai_response_id}/${currentTrackIndex}] SKIPPED. isHeader: ${isHeader}, Version: ${!!version}, GCS_URL: ${!!gcsUrlForThisVersionTrack}, Chunk_URL: ${!!chunkStreamUrlForThisVersionTrack}, UpdatingSelection: ${isUpdatingSelection}`);
      return;
    }
    e.stopPropagation();
    console.log(`[SingleVersion ItemClick ${ai_response_id}/${currentTrackIndex}] CALLED. isThisVersionPlaying (derived): ${isThisVersionPlaying}. Context isPlaying: ${isContextPlaying}, Context currentAudioSrc: ${currentAudioSrcFromContext}`);

    const detailsData = {
      name,
      description: musicDescription,
      coverImage: cover,
      duration,
      audioSrc: chunkStreamUrlForThisVersionTrack || gcsUrlForThisVersionTrack, // Use the stream URL
      ai_response_id,
      projectId: version.projectId,
      responseType,
      regenerated,
      created_at,
      selectedTrackIndexForPreview: currentTrackIndex
    };
    // console.log("Row clicked for ai_response_id:", ai_response_id); // Original log

    updateLocalState("currentSongDetails", detailsData);
    updateLocalState("audioImgSrc", cover); // <-- Add this line
    console.log(`[SingleVersion ItemClick ${ai_response_id}/${currentTrackIndex}] Updated currentSongDetails, audioImgSrc. Set context discoverId to null, aiResponseId to ${ai_response_id}.`);
    setDiscoverId(null);
    setAiResponseId(ai_response_id);

    if (!isThisVersionPlaying) {
      console.log(`[SingleVersion ItemClick ${ai_response_id}/${currentTrackIndex}] Track not playing or different source. Will attempt to play. isUpdatingSelection will be set to true.`);
      // Logic to play: update DB then set localState request
      setIsUpdatingSelection(true);
      setToastMessage(null);
      try {
        console.log(`[SingleVersion ItemClick ${ai_response_id}/${currentTrackIndex}] Updating DB: response ${ai_response_id} to selected_track_id ${currentTrackIndex}.`);
        await apiService.updateSelectedTrack({
          responseId: ai_response_id,
          trackId: currentTrackIndex,
        });
        console.log(`[SingleVersion ItemClick ${ai_response_id}/${currentTrackIndex}] DB updated. Requesting play via localState for trackIndex ${currentTrackIndex}.`);

        updateLocalState("playRequestFromHistory", {
          responseId: ai_response_id,
          trackIndex: currentTrackIndex,
          timestamp: Date.now()
        });

      } catch (error) {
        console.error(`[SingleVersion ItemClick ${ai_response_id}/${currentTrackIndex}] Error updating DB for ${ai_response_id}, track ${currentTrackIndex}:`, error);
        Sentry.captureException(error);
        setToastMessage("Error preparing track for playback. Please try again.");
        setToastType("error");
      } finally {
        console.log(`[SingleVersion ItemClick ${ai_response_id}/${currentTrackIndex}] Setting isUpdatingSelection to false.`);
        setIsUpdatingSelection(false);
      }
    }
  }; const handleDropdownToggle = () => {
    // Toggle dropdown
    setShowDropdown(!showDropdown);

    if ((isLastItem || isSecondLastItem) && !showDropdown && !isHeader) {
      setTimeout(() => {
        const versionHistoryContainer = document.querySelector(
          ".scrollable-version"
        );
        if (versionHistoryContainer) {
          versionHistoryContainer.scrollTo({
            top: versionHistoryContainer.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 50);
    }
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionClick = async (option) => {
    if (option === "Download") {

      if (!isActionEnabled) {
        setToastMessage("Please Select This Track Version in the Chat to Download.");
        setToastType("info");
        setShowDropdown(false);
        return;
      }
      setIsDownloadModalOpen(true);
    } else if (option === "Go to prompt") {
      posthog.capture("Click on Go to prompt", {
        is_user_logged_in: true,
      });
      updateLocalState("scrollToUserInput", parent_response_id);
    } else if (option === "Get stems") {
      if (!isActionEnabled) {
        setToastMessage("Please Select This Track Version in the Chat to Download Stems.");
        setToastType("info");
        setShowDropdown(false);
        return;
      }
      posthog.capture("Click on Get stems", {
        is_user_logged_in: true,
        trackName: name
      });
      handleGetStemsClick();
    }
    setShowDropdown(false);
  };

  const handleGetStemsClick = async () => {
    if (!ai_response_id || isUpdatingSelection) {
      setToastMessage("Cannot generate stems. Audio information missing or operation in progress.");
      setToastType("error");
      return;
    }

    setIsUpdatingSelection(true);
    setToastMessage("Preparing track for stem generation...");
    setToastType("info");

    try {
      await apiService.updateSelectedTrack({
        responseId: ai_response_id,
        trackId: currentTrackIndex,
      });
      console.log(`[Version GetStems] Ensured track ${currentTrackIndex} for ${ai_response_id} is selected in DB.`);
      setToastMessage(null);
    } catch (error) {
      console.error(`[Version GetStems] Error ensuring track selection for ${ai_response_id}:`, error);
      Sentry.captureException(error);
      setToastMessage("Failed to prepare track for stem generation.");
      setToastType("error");
      setIsUpdatingSelection(false);
      return;
    }

    setIsGeneratingStems(true);
    setStemProgress(0);

    console.log(`[Version] Initiating stem generation for response ID: ${ai_response_id}, Track Index: ${currentTrackIndex}`);

    try {
      setStemProgress(1);
      const gcsResponse = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/ai-response-gcs-uri/${ai_response_id}/${currentTrackIndex}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
          }
        }
      );
      const gcsData = await gcsResponse.json();

      if (!gcsResponse.ok || !gcsData.success || !gcsData.gcsUri) {
        console.error("[Version] GCS URI Extraction Failed:", { status: gcsResponse.status, data: gcsData });
        setToastMessage(gcsData.message || "Could not find the audio file URI needed for stems.");
        setToastType("error");
        setIsGeneratingStems(false);
        return;
      }
      const gcsUri = gcsData.gcsUri;
      console.log(`[Version] GCS URI: ${gcsUri}`);

      setStemProgress(2);
      setToastMessage(`${stemTrackName || 'Version'} - Stems are being generated...`);
      setToastType("info");

      const requestId = Date.now().toString();
      const processResponse = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/process-audio`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
          },
          body: JSON.stringify({ gcs_uri: gcsUri, request_id: requestId })
        }
      );
      const processData = await processResponse.json();

      if (!processResponse.ok || !processData.success) {
        console.error("[Version] Stem separation failed:", { status: processResponse.status, data: processData });
        setToastMessage(processData.message || processData.details || "Stem separation failed.");
        setToastType("error");
        setIsGeneratingStems(false);
        return;
      }

      console.log("[Version] Stem separation successful:", processData);
      setStemProgress(3);
      setToastMessage("Stems generated! Downloading...");
      setToastType("info");

      const stemPaths = Object.values(processData.uploaded_files);
      const downloadResponse = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/download-stems`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
          },
          body: JSON.stringify({ stems: stemPaths })
        }
      );

      if (downloadResponse.ok) {
        setStemTrackName(name);
        const blob = await downloadResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const finalName = name || 'Audio';
        a.download = `${finalName}_Stems_by_Wubble.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setToastMessage("Your stems have been downloaded successfully!");
        setToastType("success");
        setTimeout(() => setToastMessage(null), 5000);

      } else {
        console.error("[Version] Error downloading stems:", { status: downloadResponse.status });
        setToastMessage("Stems generated but download failed.");
        setToastType("error");
      }

    } catch (error) {
      console.error("[Version] Error during stem process:", error);
      Sentry.captureException(error);
      if (!toastMessage) {
        setToastMessage("An unexpected error occurred during stem generation.");
        setToastType("error");
      }
    } finally {
      setIsGeneratingStems(false);
      setIsUpdatingSelection(false);
      if (toastType === 'error' || (toastType === 'info' && stemProgress < 3)) {
        setTimeout(() => setToastMessage(null), 8000);
      }
    }
  };

  return (
    <>
      <div
        id={`version-${ai_response_id}-track-${currentTrackIndex}`}
        ref={dropdownRef}
        onClick={handleVersionItemClick}
        className={`flex items-center w-full px-2 py-2 rounded-lg group transition duration-500 relative
                    ${!isHeader ? 'hover:bg-gradient-to-r hover:from-purple-600/20 hover:via-purple-700/20 cursor-pointer' : ''}
                    ${isThisVersionPlaying && !isHeader ? 'bg-gradient-to-r from-purple-600/20 via-purple-700/20' : ''}`}
      >
        <div className="flex gap-2 items-center relative">
          {showVersionIcon && !isLastItem && !isHeader && (
            <div className="absolute left-[0.7rem] top-[2rem] h-full z-10">
              <div className="h-full w-0.5 [background:var(--Color-Opacity-White-10,rgba(255,255,255,0.10))]" />
            </div>
          )}
          {showVersionIcon && (
            <div className="flex items-center px-1 w-6">
              {regenerated ? (
                <ArrowsRotateRight className="w-4 h-4" />
              ) : responseType === "extension" ? (
                <img
                  src="/Change Duration.svg"
                  alt="Extended"
                  className="w-4 h-4"
                />
              ) : responseType === "edit" ? (
                <PencilToLine className="w-4 h-4" />
              ) : (
                <img
                  src="/Ellipse.svg"
                  alt="Original"
                  className="w-3 h-3 ml-0.5"
                />
              )}
            </div>
          )}
          <div className="flex items-center relative overflow-hidden w-10 h-10">
            <img
              src={cover}
              alt="Cover"
              className="w-10 h-10 flex justify-center items-center gap-2 object-cover rounded-md"
            />
            {!isHeader && (
              <button
                id="play-pause-button"
                onClick={handlePlayPauseClick}
                className={`absolute inset-0 flex items-center justify-center text-white transition-opacity duration-300 opacity-0 group-hover:opacity-100 bg-black/40 rounded-md ${isThisVersionPlaying ? "opacity-100" : ""
                  }`}
              >
                {isThisVersionPlaying ? (
                  <PauseFill className="w-4 h-4" />
                ) : (
                  <PlayFill className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        </div>

        <div className="flex-col items-start mx-2 w-full min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-extralight text-sm text-white line-clamp-1">
              {name}
            </div>
          </div>
          <div className="text-xs text-[#878188] w-full">
            {isHeader ? versionCountText : getTimeAgo(created_at)}
          </div>
        </div>        <div className="flex items-center gap-2 flex-shrink-0">
          {!isHeader && (
            <span className="font-extralight text-xs text-white">{duration}</span>
          )}
          {isHeader && (
            <ChevronDown className={`transform transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} />
          )}
          {!isHeader && (
            <button
              id="dropdown-button"
              className="ml-2 rounded-full text-[#878188] hover:text-white p-1.5 bg-transparent hover:bg-white/5 relative"
              onClick={(e) => {
                e.stopPropagation();
                handleDropdownToggle();
              }}
            >
              <Ellipsis />
            </button>
          )}</div>
        {showDropdown && !isHeader && (
          <div
            id="version-item-dropdown"
            className={`absolute p-1 right-2 ${isLastItem || isSecondLastItem ? 'bottom-10 mb-1' : 'top-10'} text-center font-normal text-sm text-[#878188] bg-[#100f10] rounded-lg border border-solid border-[#2c2a2c] z-10`}
          >
            <button
              id="go-to-prompt-button"
              className="flex items-center gap-2 h-8 w-full px-2 hover:text-white rounded-lg text-center hover:bg-zinc-900"
              onClick={(e) => {
                e.stopPropagation();
                handleOptionClick("Go to prompt");
              }}
            >
              <ArrowUturnCwRight className="w-4 h-4" /> Go to prompt
            </button>
            {/* <button
              id="get-stems-button"
              className="flex items-center gap-2 h-8 w-full px-2 group hover:text-white rounded-lg text-center hover:bg-zinc-900"
              disabled={!isActionEnabled}
              style={!isActionEnabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              onClick={(e) => {
                e.stopPropagation();
                handleOptionClick("Get stems");
                posthog.capture("Click-GetStems-VersionHistory-ChatPage")
              }}
            >
              <img
                src="/updates-modal/feature-update-icon-2.svg"
                alt="Get stems"
                className="w-4 h-4 opacity-50 group-hover:opacity-100"
              /> Get stems
            </button>
            <button
              id="download-button"
              className="flex items-center gap-2 h-8 w-full px-2 hover:text-white rounded-lg text-center hover:bg-zinc-900"

              disabled={!isActionEnabled}
              style={!isActionEnabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              onClick={(e) => {
                e.stopPropagation();
                handleOptionClick("Download");
                posthog.capture("Click-Download-VersionHistory-ChatPage")
              }}
            >
              <ArrowShapeDownToLine className="w-4 h-4" /> Download
            </button> */}
          </div>
        )}
      </div>
      {isDownloadModalOpen && (
        <DownloadModal
          title={name}
          description={musicDescription}
          duration={typeof duration === "string" ? duration : formatTime(duration) || "0:00"}
          imageSrc={avatar}
          ai_response_id={ai_response_id}
          trackIndex={currentTrackIndex}
          onClose={() => setIsDownloadModalOpen(false)}
          onError={(message) => {
            setToastMessage(message);
            setIsDownloadModalOpen(false);
          }}
        />
      )}
      {isGeneratingStems && (
        <ProgressInfoToast
          type="info"
          message={`${stemTrackName || 'Version'} - Stems are being generated...`}
          loading={true}
          excludeCloseButton={false}
          className="-translate-x-1/2 w-[90%] md:w-fit md:min-w-[360px]"
          imageSrc={avatar}
          currentStep={stemProgress}
          totalSteps={3}
          onClose={() => setIsGeneratingStems(false)}
        />
      )}
    </>
  );
};

export default Version;
